/**
 * The navigator: ring → planet → claim, plus the bucket for everything
 * no ring reaches.
 *
 * Every count here is a count of CATALOG UNITS — the thing a reviewer
 * reads — rolled up per level of the tree by the server so the three
 * levels cannot disagree about the same shelf.
 *
 * "Outside the rings" is drawn as a ring, on purpose.  Those planets
 * are not a different kind of thing — they are planets whose placement
 * is missing — and giving them their own shape would ask the reader to
 * learn a second way of looking at the same content.  What they get
 * instead is a REASON badge, because "not drawn yet" and "does not
 * exist" want different repairs from different people.
 */

import { useState } from "react";

import { needsReview, type LibraryQuery, type Scope } from "./filters";
import type { PlanetShelf, RingShelf, ShelvesPayload } from "./types";
import { Badge, Id, Label, MONO } from "./ui";

interface Props {
  shelves: ShelvesPayload;
  query: LibraryQuery;
  onScope: (scope: Partial<Scope>) => void;
  /** Hide planets a human has already read and whose content has not moved. */
  onlyUnreviewed: boolean;
}

/**
 * How a planet's SHELF stands, as one badge.
 *
 * A planet has no verdict of its own — it is a place, and places are
 * not reviewed, their contents are.  So what it carries is a count:
 * what is flagged (needs work), or that everything has been read.  A
 * planet with unread units and nothing flagged carries nothing, because
 * a navigator that badged thirty-four "unread" rows would be noise
 * exactly where the reader is scanning for the exceptions.
 */
export function ShelfBadge({ planet }: { planet: PlanetShelf }) {
  if (!planet.exists) return <Badge tone="bad">no row</Badge>;
  if (planet.flagged) {
    return (
      <Badge tone="warn" title={`${planet.flagged} flagged`}>
        {planet.flagged} flagged
      </Badge>
    );
  }
  if (planet.units > 0 && planet.unread === 0) return <Badge tone="good">read</Badge>;
  return null;
}


function OutsideBadge({ reason }: { reason: string }) {
  if (reason === "unplaced") {
    return <Badge tone="warn" title="a real planet standing on no ring">unplaced</Badge>;
  }
  if (reason === "phantom") {
    return <Badge tone="bad" title="units name a planet the registry has no row for">phantom</Badge>;
  }
  if (reason === "unaddressed") {
    return <Badge tone="bad" title="units whose claims name no planet at all">unaddressed</Badge>;
  }
  return null;
}

/** "4 lesson_bench · 2 drills" — what a planet's shelf is made of. */
function vesselMix(planet: PlanetShelf): string {
  const parts = Object.entries(planet.byVessel)
    .filter(([, count]) => count > 0)
    .map(([vessel, count]) => `${count} ${vessel}`);
  return parts.length ? parts.join(" · ") : "no units on this shelf";
}

function PlanetRow({
  planet,
  query,
  onScope,
}: {
  planet: PlanetShelf;
  query: LibraryQuery;
  onScope: (scope: Partial<Scope>) => void;
}) {
  const selected = query.scope.planet === planet.id;
  const outside = Boolean(planet.outsideReason);
  return (
    <li>
      <button
        type="button"
        data-testid={`shelf-planet-${planet.id}`}
        data-selected={selected ? "true" : "false"}
        // The vessel mix rides the tooltip rather than the row: it is
        // what a reviewer wants before they commit to a shelf, and it is
        // four more words than a navigator row can hold.
        title={vesselMix(planet)}
        onClick={() =>
          onScope(
            outside
              ? { outside: true, planet: planet.id }
              : { ring: planet.ring, planet: planet.id },
          )
        }
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          minHeight: 36,
          padding: "6px 10px 6px 22px",
          border: "none",
          borderRadius: 8,
          textAlign: "left",
          cursor: "pointer",
          background: selected ? "var(--color-primary-muted)" : "transparent",
          color: selected ? "var(--color-primary)" : "var(--color-text-secondary)",
          fontFamily: "inherit",
          fontSize: 13,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {planet.title || planet.id}
        </span>
        <ShelfBadge planet={planet} />
        {outside ? <OutsideBadge reason={planet.outsideReason} /> : null}
        {/* Units on this planet by their primary claim's address, on no
            roster: reachable in the tree and still a defect, so counted
            rather than hidden or moved somewhere they do not belong. */}
        {planet.strayUnits ? (
          <Badge tone="bad" title={`${planet.strayUnits} unit(s) whose primary claim no roster holds`}>
            {planet.strayUnits} stray
          </Badge>
        ) : null}
        <span style={{ fontFamily: MONO, fontSize: 11, color: "var(--color-text-muted)" }}>
          {planet.units}
        </span>
      </button>

      {selected && planet.claims.length > 0 ? (
        <ul style={{ listStyle: "none", margin: "2px 0 6px", padding: 0 }}>
          {planet.claims.map((claim) => {
            const on = query.scope.claim === claim.id;
            return (
              <li key={claim.id}>
                <button
                  type="button"
                  data-testid={`shelf-claim-${claim.id}`}
                  onClick={() =>
                    onScope(
                      outside
                        ? { outside: true, planet: planet.id, claim: claim.id }
                        : { ring: planet.ring, planet: planet.id, claim: claim.id },
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    minHeight: 30,
                    padding: "4px 10px 4px 34px",
                    border: "none",
                    background: "transparent",
                    borderLeft: `2px solid ${on ? "var(--color-primary)" : "transparent"}`,
                    textAlign: "left",
                    cursor: "pointer",
                    color: on ? "var(--color-primary)" : "var(--color-text-muted)",
                    fontFamily: "inherit",
                    fontSize: 12,
                  }}
                >
                  {/* Mono is for identifiers.  A claim that has a
                      title is prose and reads as prose; one that has
                      none falls back to its id, which is the only thing
                      a reader has to match against. */}
                  {claim.title ? (
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {claim.title}
                    </span>
                  ) : (
                    <Id style={{ flex: 1, minWidth: 0, fontSize: 11.5 }}>{claim.id}</Id>
                  )}
                  {claim.tombstoned ? <Badge tone="bad">drained</Badge> : null}
                  {/* A unit is shelved by its PRIMARY claim, so these
                      add up to the planet's count exactly — the claim
                      filter and this number can never disagree. */}
                  <span style={{ fontFamily: MONO, fontSize: 11 }}>{claim.units}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}

function Ring({
  id,
  name,
  level,
  units,
  flagged,
  planets,
  query,
  onScope,
  onlyUnreviewed,
  outside,
}: {
  id: string;
  name: string;
  level: number | null;
  /** Rolled up over the ring's planets — the server's sum, not ours. */
  units: number;
  flagged: number;
  planets: PlanetShelf[];
  query: LibraryQuery;
  onScope: (scope: Partial<Scope>) => void;
  onlyUnreviewed: boolean;
  outside?: boolean;
}) {
  const shown = onlyUnreviewed ? planets.filter((p) => needsReview(p)) : planets;
  const selected = outside ? query.scope.outside && !query.scope.planet : query.scope.ring === id && !query.scope.planet;
  const [open, setOpen] = useState(true);

  // A ring emptied by the review filter is REPORTED, not hidden: "you
  // have read all seven of these" is the answer the reviewer came for,
  // and a ring that silently vanished would read as content loss.
  return (
    <li style={{ marginBottom: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <button
          type="button"
          aria-label={open ? `collapse ${name || id}` : `expand ${name || id}`}
          onClick={() => setOpen((was) => !was)}
          style={{
            width: 22,
            height: 30,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "var(--color-text-muted)",
            fontSize: 10,
          }}
        >
          {open ? "▾" : "▸"}
        </button>
        <button
          type="button"
          data-testid={outside ? "shelf-outside" : `shelf-ring-${id}`}
          onClick={() => onScope(outside ? { outside: true } : { ring: id })}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flex: 1,
            minHeight: 32,
            padding: "4px 10px",
            border: "none",
            borderRadius: 8,
            textAlign: "left",
            cursor: "pointer",
            background: selected ? "var(--color-primary-muted)" : "transparent",
            color: selected ? "var(--color-primary)" : "var(--color-text)",
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>{name || id}</span>
          {/* The ring's own rollup, so a reader folding the planets away
              still knows how much is behind the fold and whether any of
              it needs work. */}
          {flagged ? <Badge tone="warn">{flagged} flagged</Badge> : null}
          {level !== null ? (
            <span style={{ fontFamily: MONO, fontSize: 10.5, color: "var(--color-text-muted)" }}>
              ring {level}
            </span>
          ) : null}
          <span style={{ fontFamily: MONO, fontSize: 11, color: "var(--color-text-muted)" }}>
            {units}
          </span>
        </button>
      </div>
      {open ? (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {shown.map((planet) => (
            <PlanetRow key={planet.id} planet={planet} query={query} onScope={onScope} />
          ))}
          {shown.length === 0 ? (
            <li
              style={{
                padding: "6px 10px 6px 22px",
                fontSize: 12,
                color: "var(--color-text-muted)",
                fontStyle: "italic",
              }}
            >
              {planets.length === 0 ? "no planets yet" : "every planet here has been read"}
            </li>
          ) : null}
        </ul>
      ) : null}
    </li>
  );
}

export function ShelfNav({ shelves, query, onScope, onlyUnreviewed }: Props) {
  const everything = !query.scope.ring && !query.scope.planet && !query.scope.outside;
  // The outside bucket is a LIST of planets rather than a ring row, so
  // its rollup is summed here — the one place in this tree the client
  // does arithmetic the server did not.
  const outsideFlagged = shelves.outside.reduce((sum, planet) => sum + planet.flagged, 0);
  return (
    <nav aria-label="the library's shelves" data-testid="library-shelf-nav">
      <button
        type="button"
        data-testid="shelf-everything"
        onClick={() => onScope({})}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          minHeight: 34,
          padding: "4px 10px",
          marginBottom: 8,
          border: "none",
          borderRadius: 8,
          textAlign: "left",
          cursor: "pointer",
          background: everything ? "var(--color-primary-muted)" : "transparent",
          color: everything ? "var(--color-primary)" : "var(--color-text)",
          fontFamily: "inherit",
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        <span style={{ flex: 1 }}>Everything</span>
        <span style={{ fontFamily: MONO, fontSize: 11, color: "var(--color-text-muted)" }}>
          {shelves.totals.units}
        </span>
      </button>

      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {shelves.rings.map((ring: RingShelf) => (
          <Ring
            key={ring.id}
            id={ring.id}
            name={ring.name}
            level={ring.level}
            units={ring.units}
            flagged={ring.flagged}
            planets={ring.planets}
            query={query}
            onScope={onScope}
            onlyUnreviewed={onlyUnreviewed}
          />
        ))}

        <li aria-hidden style={{ height: 1, background: "var(--color-border-subtle)", margin: "10px 0" }} />
        <Label style={{ padding: "0 10px 4px 26px" }}>outside the rings</Label>
        <Ring
          id="__outside__"
          name="Outside the rings"
          level={null}
          units={shelves.totals.outsideUnits}
          flagged={outsideFlagged}
          planets={shelves.outside}
          query={query}
          onScope={onScope}
          onlyUnreviewed={onlyUnreviewed}
          outside
        />
      </ul>
    </nav>
  );
}
